import { useEffect, useRef } from 'react';
import { groupAnalytics, setPersonProperties } from '../client';

interface UseIdentifyUserOptions {
  userId: number | string | undefined;
  organizationId?: number | string | null;
  organizationName?: string | null;
  organizationType?: string | null;
}

/**
 * Enriches the analytics person with who they turned out to be, and associates
 * them with their organization as a group.
 *
 * Deliberately NOT `identify(userId)`. The person is already identified by the
 * platform's cross-domain visitor id (see the client's bootstrap), and calling
 * identify() with a different id does not merge two already-identified people —
 * it switches to another one, which would cut the journey in half exactly at
 * the moment it becomes valuable: the sponsor who opened the start page
 * anonymously and then signed up. So the account arrives as PROPERTIES on the
 * person the visitor has been all along, and landing → start page → sign-up →
 * desk stays a single funnel.
 */
const useIdentifyUser = ({
  userId,
  organizationId,
  organizationName,
  organizationType,
}: UseIdentifyUserOptions): void => {
  const appliedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const key = `${userId}:${organizationId ?? ''}`;
    if (appliedRef.current === key) {
      return;
    }
    appliedRef.current = key;

    setPersonProperties({
      user_id: userId,
      organization_id: organizationId ?? undefined,
      organization_name: organizationName ?? undefined,
      organization_type: organizationType ?? undefined,
    });

    if (organizationId) {
      groupAnalytics('organization', String(organizationId), {
        name: organizationName ?? undefined,
        type: organizationType ?? undefined,
      });
    }
  }, [userId, organizationId, organizationName, organizationType]);
};

export default useIdentifyUser;
